import React, { Component } from 'react';
import '../styles/bills.css';
import FileSaver from 'file-saver';
import qs from 'qs';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Pagination,
  CircularProgress,
  Link as MuiLink,
  Tooltip,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { listInvoices, downloadInvoice, downloadReceipt } from '../services/billingService';

class Bills extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hits: [],
      page: 1,
      total_elem: 0,
      isLoading: false,
      menuAnchorEl: null,
      menuItem: null
    };
    this.onPageChange = this.onPageChange.bind(this);
    this.download = this.download.bind(this);
    this.downloadReceiptPdf = this.downloadReceiptPdf.bind(this);
    this.openMenu = this.openMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchPage(1);
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  onPageChange(event, value) {
    this.fetchPage(value);
  }

  fetchPage = (page) => {
    this.setState({ isLoading: true });
    const parsed = qs.parse(this.props.location?.search || '', { ignoreQueryPrefix: true });
    const websiteId = parsed.website || '';
    listInvoices(page, websiteId)
      .then((result) => {
        if (!this._isMounted) return;
        this.setState({
          hits: result.invoices || [],
          page: parseFloat(result.page_nbr) || page,
          total_elem: parseFloat(result.total_elem) || 0,
          isLoading: false
        });
      })
      .catch(() => {
        if (!this._isMounted) return;
        this.setState({ isLoading: false });
      });
  };

  download(invoice) {
    downloadInvoice(invoice.website, invoice.invoice_id)
      .then((blob) => FileSaver.saveAs(blob, invoice.number + '.pdf'))
      .catch((error) => {
        console.log(error);
      });
  }

  downloadReceiptPdf(invoice) {
    downloadReceipt(invoice.website, invoice.invoice_id)
      .then((blob) => FileSaver.saveAs(blob, invoice.number + '-recu.pdf'))
      .catch((error) => {
        console.log(error);
      });
  }

  openMenu(item, e) {
    this.setState({ menuAnchorEl: e.currentTarget, menuItem: item });
  }

  closeMenu() {
    this.setState({ menuAnchorEl: null, menuItem: null });
  }

  render() {
    const { hits, page, total_elem, isLoading } = this.state;
    const pageCount = Math.max(1, Math.ceil((total_elem || 0) / 10));

    return (
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Transactions</Typography>
          <Typography variant="body2" color="text.secondary">
            Total {new Intl.NumberFormat('fr-FR').format(parseInt(total_elem || 0, 10))}
          </Typography>
        </Stack>

        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table
              size="medium"
              stickyHeader
              aria-label="liste des transactions"
              sx={{
                tableLayout: 'fixed',
                '& tbody tr:nth-of-type(odd)': { bgcolor: 'action.hover' }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Website</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Devise</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && hits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : (
                  hits.map((invoice) => {
                    const totalStr = String(invoice.total ?? '');
                    const formattedTotal =
                      totalStr && totalStr.length > 2
                        ? `${totalStr.slice(0, -2)},${totalStr.slice(-2)}`
                        : totalStr;

                    return (
                      <TableRow key={invoice._id} hover>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Tooltip title={String(invoice.website)}>
                            <MuiLink
                              component={RouterLink}
                              to={`/super-admin/website/?website_id=${invoice.website}`}
                              underline="hover"
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%' }}
                            >
                              {invoice.website}
                            </MuiLink>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{invoice.invoice_date}</TableCell>
                        <TableCell>{formattedTotal}</TableCell>
                        <TableCell>{invoice.currency}</TableCell>
                        <TableCell align="right" sx={{ py: 0 }}>
                          <IconButton size="small" onClick={(e) => this.openMenu(invoice, e)} aria-label="Plus d'actions">
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
                {!isLoading && hits.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Aucune donnée
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              Page {page} / {pageCount}
            </Typography>
            <Pagination
              page={page || 1}
              count={pageCount}
              onChange={this.onPageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
              siblingCount={0}
              boundaryCount={1}
            />
          </Box>

          <Menu
            anchorEl={this.state.menuAnchorEl}
            open={Boolean(this.state.menuAnchorEl)}
            onClose={this.closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
          >
            <MenuItem
              onClick={() => { if (this.state.menuItem) this.download(this.state.menuItem); this.closeMenu(); }}
            >
              Télécharger la facture
            </MenuItem>
            {/* B0_59 : `status` absent (API pas encore déployée) → entrée masquée, jamais
                confondue avec « non payée ». Facture à 0 € : aucun paiement, donc aucun reçu. */}
            {this.state.menuItem?.status === 'paid' && this.state.menuItem?.total > 0 && (
              <MenuItem
                onClick={() => { if (this.state.menuItem) this.downloadReceiptPdf(this.state.menuItem); this.closeMenu(); }}
              >
                Télécharger le reçu
              </MenuItem>
            )}
            <MenuItem
              component={RouterLink}
              to={this.state.menuItem ? `/super-admin/website/?website_id=${this.state.menuItem.website}` : '#'}
              onClick={this.closeMenu}
            >
              Ouvrir communauté
            </MenuItem>
          </Menu>
        </Paper>
      </Box>
    );
  }
}

export default Bills;